import React from "react";
import SortableHeader from "../HotelPage/SortableHeader";
import { QR_SORT_COLUMNS, formatQRDate, resolveDemoBaseUrl } from "../utils/greenMilesUtils";

const DemoRoomsTable = ({ rooms = [], sortState, handleSort, onEditQr }) => {
  if (rooms.length === 0) {
    return (
      <div className="bg-white shadow-md rounded-lg p-8 text-center">
        <div className="text-gray-500 text-lg">Aucune chambre</div>
      </div>
    );
  }

  const roomsWithQR = rooms.filter((room) => room.has_qr_code === 1);
  const roomsWithoutQR = rooms.filter((room) => room.has_qr_code === 0);

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader column={QR_SORT_COLUMNS.ROOM_NUMBER} currentColumn={sortState.column} currentDirection={sortState.direction} onSort={handleSort}>
                Chambre
              </SortableHeader>
              <SortableHeader column={QR_SORT_COLUMNS.QR_CODE} currentColumn={sortState.column} currentDirection={sortState.direction} onSort={handleSort}>
                Code
              </SortableHeader>
              <SortableHeader column={QR_SORT_COLUMNS.LAST_STAY} currentColumn={sortState.column} currentDirection={sortState.direction} onSort={handleSort}>
                Dernier séjour
              </SortableHeader>
              <SortableHeader sortable={false}>Liens</SortableHeader>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rooms.map((room, index) => (
              <tr key={room.rId || index} className={`hover:bg-gray-50 transition-colors ${room.has_qr_code === 0 ? "bg-orange-25" : ""}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{room.rRoomNumber || room.room_nb || room.roomNumber}</div>
                      {room.rId && <div className="text-xs text-gray-500">ID: {room.rId}</div>}
                    </div>
                    {room.has_qr_code === 0 && (
                      <div className="ml-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Sans QR</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {room.has_qr_code === 1 ? (
                    <div className="flex items-center">
                      <div className="bg-green-50 border border-green-200 rounded-md p-2 max-w-xs">
                        <div className="text-xs font-mono text-green-800 truncate" title={room.qrId}>
                          {room.qrId}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const base = resolveDemoBaseUrl();
                          navigator.clipboard.writeText(`${base}/${room.qrId}`);
                        }}
                        className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Copier l'URL de la démo"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <button onClick={() => onEditQr?.(room)} className="px-2 py-1 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50" title="Ajouter le QR">
                        Ajouter Code
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {room.has_qr_code === 1 && room.last_stay_date ? (
                    <div>
                      <div className="text-sm text-gray-900">{formatQRDate(room.last_stay_date)}</div>
                      <div className="text-xs text-gray-500">{new Date(room.last_stay_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">{room.has_qr_code === 1 ? "Aucun séjour" : "-"}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    {room.has_qr_code === 1 ? (
                      <button
                        onClick={() => {
                          const base = resolveDemoBaseUrl();
                          window.open(`${base}/${room.qrId}`, "_blank");
                        }}
                        className="text-green-600 hover:text-green-900 transition-colors"
                        title="Ouvrir la démo"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-gray-50 px-6 py-3 border-t">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <span>Total: {rooms.length} chambres</span>
            <span className="text-green-600">Avec QR: {roomsWithQR.length}</span>
            {roomsWithoutQR.length > 0 && <span className="text-orange-600">Sans QR: {roomsWithoutQR.length}</span>}
          </div>
          {sortState.column && (
            <div className="flex items-center text-xs text-gray-500">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
              Tri: {sortState.column} ({sortState.direction === "asc" ? "croissant" : "décroissant"})
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoRoomsTable;
